import React from 'react';
import {
  GridActionsCellItem,
  GridColDef,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

import { FGrid, FGridMoneyCell } from '../grid';
import { FGridCodeCombo } from '../grid/FGridCodeCombo';

type Props = {
  rows: any[];
  periods?: any[];
  entities?: any[];
  locations?: any[];
  entryTypes?: any[];
  drivers?: any[];
  accounts?: any[];
  loading?: boolean;
  onChange: (rows: any[]) => void;
  onSubmit: (rows: any[]) => Promise<void>;
};

type State = {
  rowModesModel: GridRowModesModel;
  saving: boolean;
};

const tempId = () => `new-bie-entry-${Date.now()}-${Math.round(Math.random() * 100000)}`;

class BieEntryGrid extends React.Component<Props, State> {
  state: State = {
    rowModesModel: {},
    saving: false,
  };

  getRowId = (row: any) => row.id || row.rid || row.recid;

  addRow = () => {
    const id = tempId();

    const row = {
      id,
      rid: null,
      etc: '',
      pdc: '',
      enc: '',
      lcc: '',
      acc: '',
      drc: '',
      ref: '',
      qty: 0,
      upr: 0,
      amt: 0,
      dsc: '',
      _state: 'new',
    };

    this.props.onChange([...(this.props.rows || []), row]);

    setTimeout(() => {
      this.setState({
        rowModesModel: {
          ...this.state.rowModesModel,
          [id]: { mode: GridRowModes.Edit },
        },
      });
    }, 0);
  };

  deleteRow = (id: any) => {
    const rows = (this.props.rows || [])
      .map((r: any) => {
        if (String(this.getRowId(r)) !== String(id)) return r;
        if (!r.rid && !r.recid) return { ...r, _state: 'discard' };
        return { ...r, _state: 'deleted', status: 0 };
      })
      .filter((r: any) => r._state !== 'discard');

    this.props.onChange(rows);
  };

  processRowUpdate = (newRow: GridRowModel) => {
    const id = this.getRowId(newRow);

    const rows = (this.props.rows || []).map((r: any) =>
      String(this.getRowId(r)) === String(id)
        ? {
            ...r,
            ...newRow,
            amt: Number(newRow.amt || 0) || Number(newRow.qty || 0) * Number(newRow.upr || 0),
            _state: (r.rid || r.recid) ? 'dirty' : 'new',
          }
        : r
    );

    this.props.onChange(rows);
    return rows.find((r: any) => String(this.getRowId(r)) === String(id)) || newRow;
  };

  saveAll = async () => {
    this.setState({ saving: true });
    try {
      await this.props.onSubmit(this.props.rows || []);
    } finally {
      this.setState({ saving: false });
    }
  };

  edit = (id: any) => {
    this.setState({ rowModesModel: { ...this.state.rowModesModel, [id]: { mode: GridRowModes.Edit } } });
  };

  save = (id: any) => {
    this.setState({ rowModesModel: { ...this.state.rowModesModel, [id]: { mode: GridRowModes.View } } });
  };

  cancel = (id: any) => {
    this.setState({ rowModesModel: { ...this.state.rowModesModel, [id]: { mode: GridRowModes.View, ignoreModifications: true } } });
  };

  actions = (params: any) => {
    const isEditing = this.state.rowModesModel[params.id]?.mode === GridRowModes.Edit;

    if (isEditing) {
      return [
        <GridActionsCellItem key="save" icon={<SaveIcon />} label="Save" onClick={() => this.save(params.id)} />,
        <GridActionsCellItem key="cancel" icon={<CancelIcon />} label="Cancel" onClick={() => this.cancel(params.id)} />,
      ];
    }

    return [
      <GridActionsCellItem key="edit" icon={<EditIcon />} label="Edit" onClick={() => this.edit(params.id)} />,
      <GridActionsCellItem key="delete" icon={<DeleteIcon />} label="Delete" onClick={() => this.deleteRow(params.id)} />,
    ];
  };

  codeEditor = (itemsKey: keyof Props, label: string) => (params: any) => (
    <FGridCodeCombo
      label={label}
      items={(this.props[itemsKey] as any[]) || []}
      value={params.value || ''}
      type="CODE"
      autoOpen
      {...params}
    />
  );

  columns = (): GridColDef[] => [
    { field: 'etc', headerName: 'Type', width: 120, editable: true, renderEditCell: this.codeEditor('entryTypes', 'Type') },
    { field: 'pdc', headerName: 'Period', width: 120, editable: true, renderEditCell: this.codeEditor('periods', 'Period') },
    { field: 'enc', headerName: 'Entity', width: 150, editable: true, renderEditCell: this.codeEditor('entities', 'Entity') },
    { field: 'lcc', headerName: 'Location', width: 150, editable: true, renderEditCell: this.codeEditor('locations', 'Location') },
    { field: 'acc', headerName: 'Account', width: 130, editable: true, renderEditCell: this.codeEditor('accounts', 'Account') },
    { field: 'drc', headerName: 'Driver', width: 150, editable: true, renderEditCell: this.codeEditor('drivers', 'Driver') },
    { field: 'ref', headerName: 'Reference', width: 180, editable: true },
    { field: 'qty', headerName: 'Quantity', width: 120, type: 'number', editable: true },
    { field: 'upr', headerName: 'Unit Price', width: 130, type: 'number', editable: true },
    { field: 'amt', headerName: 'Amount', width: 150, type: 'number', editable: true, renderCell: FGridMoneyCell },
    { field: 'dsc', headerName: 'Description', flex: 1, minWidth: 240, editable: true },
    { field: 'actions', type: 'actions', width: 100, getActions: this.actions },
  ];

  render() {
    return (
      <FGrid
        title="Manual Simulation Entries"
        subtitle="Enter or edit simulation parameters directly when file import is not required."
        rows={this.props.rows || []}
        columns={this.columns()}
        loading={this.props.loading || this.state.saving}
        height={520}
        addRow={this.addRow}
        saveAll={this.saveAll}
        showQuickFilter
        toolbarSx={{ backgroundColor: '#e7f6ea' }}
        getRowId={this.getRowId}
        editMode="row"
        rowModesModel={this.state.rowModesModel}
        onRowModesModelChange={(model: GridRowModesModel) => this.setState({ rowModesModel: model })}
        processRowUpdate={this.processRowUpdate}
        onProcessRowUpdateError={(err: any) => console.error(err)}
        pageSizeOptions={[25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
      />
    );
  }
}

export default BieEntryGrid;
